import { useEffect, useId, useRef } from 'react';
import './jqueryGlobals';
import $ from 'jquery';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import select2Factory from 'select2';
(select2Factory as unknown as (w: Window, jq: typeof $) => void)(window, $);

import type { Select2AsyncItem, Select2Page } from './Select2Async';

interface Select2AsyncMultiProps {
  label?: string;
  placeholder?: string;
  className?: string;
  minInputLength?: number;
  // How many results per page the dropdown pulls on scroll (passed to the loader).
  pageSize?: number;
  closeOnSelect?: boolean;
  disabled?: boolean;
  // Paginated remote loader: returns a page of items + whether more pages exist.
  fetch: (term: string, page: number) => Promise<Select2Page>;
  // Controlled selection: array of selected values. onChange returns the strings.
  value: string[];
  onChange: (value: string[]) => void;
  // Display labels for pre-seeded values (so chips render without a fetch).
  valueLabels?: Record<string, string>;
}

// Async paginated MULTI-select (real select2 `multiple` + ajax transport).
// Combines Select2Async (remote, paged) with Select2Multi (array value) — the
// exception picker + Add Group/Login + Edit Exceptions dialogs need all three.
export function Select2AsyncMulti({
  label,
  placeholder = 'Search…',
  className,
  minInputLength = 0,
  closeOnSelect = false,
  disabled = false,
  fetch,
  value,
  onChange,
  valueLabels,
}: Select2AsyncMultiProps) {
  const id = useId();
  const selectRef = useRef<HTMLSelectElement>(null);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueLabelsRef = useRef(valueLabels);
  valueLabelsRef.current = valueLabels;

  const valueKey = value.join(',');

  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ($el as any).select2({
      width: '100%',
      placeholder,
      allowClear: true,
      multiple: true,
      closeOnSelect,
      minimumInputLength: minInputLength,
      ajax: {
        delay: 250,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transport: (params: any, success: any, failure: any) => {
          const term = params?.data?.term ?? '';
          const page = params?.data?.page ?? 1;
          fetchRef.current(term, page)
            .then((res: Select2Page) =>
              success({
                results: res.items.map((i: Select2AsyncItem) => ({ id: i.value, text: i.text })),
                pagination: { more: res.hasMore },
              }),
            )
            .catch(() => failure && failure());
          return { abort: () => {} };
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        processResults: (data: any) => data,
      },
    });

    // Pre-seed current values so chips display without a fetch.
    seedOptions(el, value, valueLabelsRef.current);
    $el.val(value).trigger('change.select2');

    const onSel = () => {
      const v = ($el.val() as string[] | null) ?? [];
      onChangeRef.current(v.map(String));
    };
    $el.on('change', onSel);

    return () => {
      $el.off('change', onSel);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (($el as any).data('select2')) ($el as any).select2('destroy');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, minInputLength, closeOnSelect, disabled]);

  // Keep controlled selection in sync when `value` changes externally.
  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;
    const $el = $(el);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!($el as any).data('select2')) return;
    const current = (($el.val() as string[] | null) ?? []).map(String).sort().join(',');
    if (value.slice().sort().join(',') === current) return;
    seedOptions(el, value, valueLabelsRef.current);
    $el.val(value).trigger('change.select2');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  return (
    <div className={className || undefined}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
        </label>
      )}
      <select id={id} ref={selectRef} className="form-control" multiple disabled={disabled} />
    </div>
  );
}

function seedOptions(el: HTMLSelectElement, values: string[], labels?: Record<string, string>) {
  values.forEach((v) => {
    if (!el.querySelector(`option[value="${CSS.escape(v)}"]`)) {
      el.append(new Option(labels?.[v] ?? v, v, true, true));
    }
  });
}
